/**
 * The reading graph on uPlot: history line with fill and points, the 5-point
 * prediction as a dashed line fading along a gradient stroke, the confidence
 * band (bands between series 3 and 4) and the uncertainty band (5 and 6),
 * plus a draw hook for the normal band with dashed edges, the failure
 * threshold line, the TODAY line, replacement markers, the current reading
 * dot and the predicted-failure marker with its inventory box. Text values
 * show the "Graph not available" note instead.
 */
import { useCallback, useMemo } from 'react';
import uPlot from 'uplot';
import { cn } from '@/lib/cn';
import { UPlotChart } from '../../components/UPlotChart';
import { axisDefaults, useChartColours, withAlpha } from '../../components/chartTheme';
import { EmptyState } from '../../components/layout';
import { fmtShortDate } from '../../lib/format';
import { buildGraphModel, type SeriesPoint } from '../../lib/graphSeries';
import { stockBadge } from '../../lib/inventoryBadge';
import { fmtValue } from '../../lib/values';
import type { NormalRange } from '../../../types';
import type { SparePart } from './PredictionCard';

export interface ReadingGraphProps {
  points: SeriesPoint[];
  normalRange: NormalRange | null | undefined;
  unit: string;
  nowMs: number;
  replacementsMs: number[];
  inventory: SparePart | null;
  isText: boolean;
  height?: number;
}

export function ReadingGraph({ points, normalRange, unit, nowMs, replacementsMs, inventory, isText, height = 240 }: ReadingGraphProps) {
  const model = useMemo(() => (isText ? null : buildGraphModel(points, normalRange)), [points, normalRange, isText]);
  const colours = useChartColours();

  const data = useMemo<uPlot.AlignedData>(
    () => (model ? [model.xs, model.history, model.prediction, model.confidenceHigh, model.confidenceLow, model.uncertaintyHigh, model.uncertaintyLow] : [[], []]),
    [model],
  );

  const buildOptions = useCallback(
    (): Omit<uPlot.Options, 'width' | 'height'> => {
      const c = colours;
      const m = model!;
      const pr = () => uPlot.pxRatio || window.devicePixelRatio || 1;
      const predictionStroke = (u: uPlot): CanvasGradient | string => {
        const x0 = u.valToPos(m.current.t, 'x', true);
        const x1 = u.valToPos(m.xs[m.xs.length - 1], 'x', true);
        if (!Number.isFinite(x0) || !Number.isFinite(x1) || x1 <= x0) return c.caution;
        const g = u.ctx.createLinearGradient(x0, 0, x1, 0);
        g.addColorStop(0, withAlpha(c.caution, 1));
        g.addColorStop(1, withAlpha(c.caution, 0.3));
        return g;
      };
      const historyFill = (u: uPlot): CanvasGradient | string => {
        const g = u.ctx.createLinearGradient(0, u.bbox.top, 0, u.bbox.top + u.bbox.height);
        g.addColorStop(0, withAlpha(c.ink2, 0.25));
        g.addColorStop(1, withAlpha(c.ink2, 0));
        return g;
      };
      const dashedLine = (ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, stroke: string, width: number, dash: number[]) => {
        ctx.save();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = width;
        ctx.setLineDash(dash);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
        ctx.restore();
      };
      const drawUnder = (u: uPlot) => {
        if (!m.normalRange) return;
        const { left, width } = u.bbox;
        const yHigh = u.valToPos(m.normalRange.high, 'y', true);
        const yLow = u.valToPos(m.normalRange.low, 'y', true);
        const ctx = u.ctx;
        ctx.save();
        ctx.fillStyle = withAlpha(c.nominal, 0.12);
        ctx.fillRect(left, yHigh, width, yLow - yHigh);
        ctx.restore();
        const r = pr();
        dashedLine(ctx, left, yHigh, left + width, yHigh, withAlpha(c.nominal, 0.6), r, [4 * r, 4 * r]);
        dashedLine(ctx, left, yLow, left + width, yLow, withAlpha(c.nominal, 0.6), r, [4 * r, 4 * r]);
        ctx.save();
        ctx.fillStyle = withAlpha(c.nominal, 0.9);
        ctx.font = `bold ${10 * r}px "JetBrains Mono Variable", ui-monospace, monospace`;
        ctx.textAlign = 'right';
        ctx.fillText(`Normal high ${fmtValue(m.normalRange.high)}${unit}`, left + width - 4 * r, yHigh - 4 * r);
        ctx.fillText(`Normal low ${fmtValue(m.normalRange.low)}${unit}`, left + width - 4 * r, yLow + 12 * r);
        ctx.restore();
      };
      const drawOver = (u: uPlot) => {
        const ctx = u.ctx;
        const r = pr();
        const { left, top, width, height: h } = u.bbox;
        const font = (size: number, bold = false) => `${bold ? 'bold ' : ''}${size * r}px "JetBrains Mono Variable", ui-monospace, monospace`;
        if (m.failureThreshold !== null) {
          const y = u.valToPos(m.failureThreshold, 'y', true);
          if (y >= top && y <= top + h) {
            dashedLine(ctx, left, y, left + width, y, c.warning, 2 * r, [8 * r, 4 * r]);
            ctx.save();
            ctx.fillStyle = c.warning;
            ctx.font = font(10, true);
            ctx.textAlign = 'right';
            ctx.fillText('FAILURE THRESHOLD', left + width - 4 * r, y - 6 * r);
            ctx.restore();
          }
        }
        const todayX = u.valToPos(nowMs, 'x', true);
        if (todayX >= left && todayX <= left + width) {
          dashedLine(ctx, todayX, top, todayX, top + h, withAlpha(c.ink3, 0.8), 2 * r, [6 * r, 3 * r]);
          ctx.save();
          ctx.fillStyle = c.ink3;
          ctx.font = font(10, true);
          ctx.textAlign = 'center';
          ctx.fillText('TODAY', todayX, top + 10 * r);
          ctx.restore();
        }
        for (const t of replacementsMs) {
          const x = u.valToPos(t, 'x', true);
          if (x < left || x > left + width) continue;
          dashedLine(ctx, x, top, x, top + h, withAlpha(c.live, 0.8), 2 * r, [4 * r, 4 * r]);
          ctx.save();
          ctx.fillStyle = withAlpha(c.live, 0.9);
          ctx.beginPath();
          ctx.arc(x, top + 20 * r, 10 * r, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = c.raised;
          ctx.font = font(11, true);
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('R', x, top + 20 * r);
          ctx.textBaseline = 'alphabetic';
          ctx.fillStyle = c.live;
          ctx.font = font(8, true);
          ctx.fillText('REPLACED', x, top + 40 * r);
          ctx.restore();
        }
        const cx = u.valToPos(m.current.t, 'x', true);
        const cy = u.valToPos(m.current.v, 'y', true);
        ctx.save();
        ctx.fillStyle = c.nominal;
        ctx.strokeStyle = c.ink;
        ctx.lineWidth = 2 * r;
        ctx.beginPath();
        ctx.arc(cx, cy, 6 * r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = c.nominal;
        ctx.font = font(10, true);
        ctx.textAlign = cx > left + width * 0.7 ? 'right' : 'left';
        ctx.fillText(`Current ${fmtValue(m.current.v)}${unit}`, cx + (cx > left + width * 0.7 ? -10 : 10) * r, cy - 10 * r);
        ctx.restore();
        if (m.predictedFailure) {
          const fx = u.valToPos(m.predictedFailure.t, 'x', true);
          const fy = u.valToPos(m.predictedFailure.v, 'y', true);
          const s = 8 * r;
          ctx.save();
          ctx.strokeStyle = c.warning;
          ctx.lineWidth = 3 * r;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(fx - s, fy - s);
          ctx.lineTo(fx + s, fy + s);
          ctx.moveTo(fx + s, fy - s);
          ctx.lineTo(fx - s, fy + s);
          ctx.stroke();
          ctx.lineWidth = 1.5 * r;
          ctx.beginPath();
          ctx.arc(fx, fy, s + 4 * r, 0, Math.PI * 2);
          ctx.stroke();
          const label = `Predicted failure ${fmtShortDate(m.predictedFailure.t)}`;
          ctx.font = font(10, true);
          const tw = ctx.measureText(label).width;
          const lx = Math.min(Math.max(fx, left + tw / 2 + 8 * r), left + width - tw / 2 - 8 * r);
          const ly = Math.max(top + 24 * r, fy - s - 22 * r);
          ctx.fillStyle = withAlpha(c.warning, 0.9);
          ctx.fillRect(lx - tw / 2 - 6 * r, ly - 11 * r, tw + 12 * r, 16 * r);
          ctx.fillStyle = c.ink;
          ctx.textAlign = 'center';
          ctx.fillText(label, lx, ly);
          if (inventory) {
            const badge = stockBadge(inventory);
            const line1 = badge.label;
            const line2 = `Qty ${inventory.quantity}, lead ${inventory.leadTimeDays} d`;
            const bw = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + 16 * r;
            const bx = Math.min(lx - bw / 2, left + width - bw - 4 * r);
            const by = ly + 8 * r;
            const stroke = badge.tone === 'nominal' ? c.nominal : badge.tone === 'caution' ? c.caution : c.warning;
            ctx.fillStyle = withAlpha(c.raised, 0.92);
            ctx.strokeStyle = stroke;
            ctx.lineWidth = 1.5 * r;
            ctx.fillRect(bx, by, bw, 30 * r);
            ctx.strokeRect(bx, by, bw, 30 * r);
            ctx.textAlign = 'left';
            ctx.fillStyle = stroke;
            ctx.fillText(line1, bx + 8 * r, by + 12 * r);
            ctx.fillStyle = c.ink2;
            ctx.font = font(9);
            ctx.fillText(line2, bx + 8 * r, by + 24 * r);
          }
          ctx.restore();
        }
      };
      return {
        ms: 1,
        tzDate: (ts: number) => uPlot.tzDate(new Date(ts), 'Etc/UTC'),
        legend: { show: false },
        cursor: { show: true, x: true, y: false, drag: { x: false, y: false }, points: { size: 6 } },
        scales: { x: { time: true }, y: { auto: false, range: [m.yMin, m.yMax] } },
        axes: [
          { ...axisDefaults(c), values: (_u: uPlot, splits: number[]) => splits.map((v) => fmtShortDate(v)), space: 70 },
          { ...axisDefaults(c), size: 56, values: (_u: uPlot, splits: number[]) => splits.map((v) => `${fmtValue(v)}${unit}`) },
        ],
        series: [
          {},
          { label: 'History', stroke: c.ink2, width: 2, fill: historyFill, points: { show: true, size: 5, fill: c.ink2, stroke: c.ink2 } },
          { label: 'Prediction', stroke: predictionStroke, width: 2, dash: [6, 4], points: { show: true, size: 4, fill: c.caution, stroke: c.caution } },
          { label: 'Confidence high', stroke: withAlpha(c.caution, 0.4), width: 1, dash: [3, 3], points: { show: false } },
          { label: 'Confidence low', stroke: withAlpha(c.caution, 0.4), width: 1, dash: [3, 3], points: { show: false } },
          { label: 'Uncertainty high', stroke: 'transparent', width: 0, points: { show: false } },
          { label: 'Uncertainty low', stroke: 'transparent', width: 0, points: { show: false } },
        ],
        bands: [
          { series: [3, 4], fill: withAlpha(c.caution, 0.15) },
          { series: [5, 6], fill: withAlpha(c.caution, 0.07) },
        ],
        hooks: { drawClear: [drawUnder], draw: [drawOver] },
      };
    },
    [colours, model, unit, nowMs, replacementsMs, inventory],
  );

  if (isText) {
    return (
      <EmptyState title="Graph not available for text-based values.">View the readings in the Readings tab.</EmptyState>
    );
  }
  if (!model) return <EmptyState title="Not enough history to draw a graph." />;

  const legend: Array<{ label: string; swatch: string }> = [
    { label: 'Historical data', swatch: 'border-b-2 border-ink-2' },
    { label: 'Prediction', swatch: 'border-b-2 border-dashed border-caution' },
    { label: 'Confidence band', swatch: 'h-2 bg-caution/30' },
    { label: 'Current reading', swatch: 'h-2 w-2 rounded-full bg-nominal' },
    { label: 'Failure threshold', swatch: 'border-b-2 border-dashed border-warning' },
  ];
  if (model.normalRange) legend.push({ label: 'Normal band', swatch: 'h-2 bg-nominal/25' });
  if (replacementsMs.length) legend.push({ label: 'Component replaced', swatch: 'h-2 w-2 rounded-full bg-live' });

  return (
    <div className="flex flex-col gap-2" data-chart-ink={colours.ink}>
      <UPlotChart data={data} buildOptions={buildOptions} height={height} />
      <ul className="flex flex-wrap gap-x-3 gap-y-1 px-1 text-[10px] text-ink-3">
        {legend.map((l) => (
          <li key={l.label} className="flex items-center gap-1.5">
            <span className={cn('inline-block w-4', l.swatch)} />
            {l.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
